import React, { useEffect } from "react";
import { HiOutlineSparkles } from "react-icons/hi";
import { classNames } from "Utils/utils";
import { VERTEX360_COPILOT_THEME } from "../constants/vertex360CopilotTheme";
import { useVertex360CopilotWorkspace } from "../hooks/useVertex360CopilotWorkspace";
import {
  buildClarifyFollowUp,
  buildClarifyFollowUpFromAnswers,
  normalizeClarifyQuestions,
  pickClarifySelectedOption,
  sanitizeClarifyAnswers,
} from "../utils/vertex360CopilotChatUtils";
import ChatMessageList from "../components/ChatMessageList";
import MessageComposer from "../components/MessageComposer";
import { displayConversationTitle } from "../constants/vertex360CopilotWorkspaceConstants";

const Vertex360CopilotWorkspaceChat = () => {
  const {
    activeConversation,
    activeConversationId,
    composerFocusKey,
    hasConversation,
    isLoadingMessages,
    isAssistantTyping,
    isStreaming,
    streamingMessageId,
    isLoadingOlder,
    hasMoreOlder,
    scrollAnchorRef,
    sendMessage,
    selectClarifyOption,
    clearActiveFilters,
    loadOlderMessages,
    stopGeneration,
    retryMessage,
    rateMessageFeedback,
  } = useVertex360CopilotWorkspace();

  const isBusy = isAssistantTyping || isStreaming;

  const handleClarifyOption = (prompt, option, message) => {
    // Typed role (option 3) uses the free-text value; fixed options use id.
    if (option?.answers && option?.questions) {
      const questions = normalizeClarifyQuestions(option.questions);
      const answers = sanitizeClarifyAnswers(option.answers, questions);
      const optionId = pickClarifySelectedOption(questions, answers);
      const typedEntry = Object.values(answers).find(
        (ans) => ans && typeof ans === "object" && String(ans.text || "").trim()
      );
      const typed = typedEntry ? String(typedEntry.text).trim() : "";
      const displayLabel = typed;
      const serverMessageId = selectClarifyOption(
        message,
        optionId || "__typed__",
        displayLabel || undefined,
        answers
      );
      const sendOptions = {
        ...(serverMessageId ? { clarifyMessageId: serverMessageId } : {}),
        clarifySelectedOption: optionId || "__typed__",
        ...(displayLabel ? { clarifySelectedLabel: displayLabel } : {}),
        clarifyAnswers: answers,
      };
      const followUp = buildClarifyFollowUpFromAnswers(prompt, questions, answers);
      if (followUp.trim()) sendMessage(followUp, sendOptions);
      return;
    }
    const typed = String(option?.typedLabel || "").trim();
    const optionId = String(option?.id || "").trim();
    const optionLabel = String(option?.label || "").trim();
    const displayLabel = typed || optionLabel;
    // API bind uses opaque id (pt:/ep:); chat copy uses the human label.
    const choice = optionId || displayLabel;
    let sendOptions;
    if (message && optionId) {
      const serverMessageId = selectClarifyOption(
        message,
        optionId,
        displayLabel || undefined
      );
      sendOptions = {
        ...(serverMessageId ? { clarifyMessageId: serverMessageId } : {}),
        clarifySelectedOption: optionId,
        ...(displayLabel ? { clarifySelectedLabel: displayLabel } : {}),
      };
    }
    const followUp = buildClarifyFollowUp(prompt, choice, displayLabel);
    if (followUp.trim()) sendMessage(followUp, sendOptions);
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isBusy) {
        stopGeneration();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isBusy, stopGeneration]);

  const messages = activeConversation?.messages || [];
  const firstUserText = (messages.find((item) => item?.role === "user") || {})
    .content;
  const title = displayConversationTitle(
    activeConversation?.title,
    firstUserText
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div
        className={classNames(
          "shrink-0 border-b px-5 py-3.5",
          VERTEX360_COPILOT_THEME.headerBg
        )}
      >
        <div className="flex items-center gap-3">
          <span
            className={classNames(
              "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-md shadow-[#4B6CB7]/20",
              VERTEX360_COPILOT_THEME.heroGradient
            )}
          >
            <HiOutlineSparkles className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-slate-900">{title}</h1>
            {/* <p className="text-xs text-slate-500">Organisation-wide AI agent</p> */}
          </div>
        </div>
      </div>

      <ChatMessageList
        messages={messages}
        hasConversation={hasConversation}
        isLoadingMessages={isLoadingMessages}
        conversationId={
          activeConversation?.serverId || activeConversationId || null
        }
        isAssistantTyping={isAssistantTyping}
        isStreaming={isStreaming}
        streamingMessageId={streamingMessageId}
        isLoadingOlder={isLoadingOlder}
        hasMoreOlder={hasMoreOlder}
        onLoadOlder={loadOlderMessages}
        onSelectPrompt={sendMessage}
        onRetryMessage={retryMessage}
        onClarifyOption={handleClarifyOption}
        onMessageRated={rateMessageFeedback}
        scrollContainerRef={scrollAnchorRef}
      />

      <MessageComposer
        key={activeConversationId || "copilot-draft"}
        onSend={sendMessage}
        onStop={stopGeneration}
        disabled={false}
        isSending={isBusy}
        focusRequestKey={composerFocusKey}
        activeFilters={activeConversation?.activeFilters || null}
        contextSummary={activeConversation?.contextSummary || null}
        onClearFilters={clearActiveFilters}
      />
    </div>
  );
};

export default Vertex360CopilotWorkspaceChat;
